// $lib/db/indexeddb.js (updated)

// constants
const DB_NAME = "App2KSecureDB";
const STORE_NAME = "authStore";
const KEY_STORE = "cryptoKeys";
const TOKEN_KEY = "refreshToken_encrypted";
const CRYPTO_KEY_ID = "aes-key";

// localStorage backup keys (base64)
const LS_KEY_BACKUP = "__app2k_aes_key_b64";
const LS_TOKEN_BACKUP = "__app2k_refresh_token_b64";

let cryptoKey = null;

// helpers (ArrayBuffer <-> base64)
function arrayBufferToBase64(ab) {
    const u8 = new Uint8Array(ab);
    let binary = "";
    for (let i = 0; i < u8.length; i++) binary += String.fromCharCode(u8[i]);
    return btoa(binary);
}

function base64ToArrayBuffer(b64) {
    const binary = atob(b64);
    const len = binary.length;
    const u8 = new Uint8Array(len);
    for (let i = 0; i < len; i++) u8[i] = binary.charCodeAt(i);
    return u8.buffer;
}

// ===============================
// openDB (no change except handling blocked)
function openDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 3);

        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
            if (!db.objectStoreNames.contains(KEY_STORE)) db.createObjectStore(KEY_STORE);
        };

        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        req.onblocked = () => {
            console.warn("IndexedDB blocked");
            reject(new Error("Database blocked"));
        };
    });
}

// ===============================
// saveExportedKey + backup to localStorage (base64)
async function saveExportedKey(keyRaw) {
    try {
        const db = await openDB();
        const tx = db.transaction(KEY_STORE, "readwrite");
        tx.objectStore(KEY_STORE).put(keyRaw, CRYPTO_KEY_ID);

        // backup to localStorage as base64
        try {
            const b64 = arrayBufferToBase64(keyRaw);
            localStorage.setItem(LS_KEY_BACKUP, b64);
        } catch (e) {
            console.warn("Backup key to localStorage failed:", e);
        }

        return new Promise((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => {
                console.warn("saveExportedKey tx error:", tx.error);
                reject(tx.error);
            };
        });
    } catch (err) {
        console.warn("saveExportedKey fallback -> store in localStorage only", err);
        // fallback: store to localStorage only
        try {
            const b64 = arrayBufferToBase64(keyRaw);
            localStorage.setItem(LS_KEY_BACKUP, b64);
            return;
        } catch (e) {
            throw err;
        }
    }
}

// ===============================
// loadExportedKey (try IndexedDB -> fallback localStorage)
async function loadExportedKey() {
    try {
        const db = await openDB();
        const tx = db.transaction(KEY_STORE, "readonly");
        const req = tx.objectStore(KEY_STORE).get(CRYPTO_KEY_ID);

        return await new Promise((resolve) => {
            req.onsuccess = () => {
                const result = req.result;
                if (result instanceof ArrayBuffer) {
                    resolve(result);
                } else if (result && result.buffer instanceof ArrayBuffer) {
                    // case when stored as Uint8Array
                    resolve(result.buffer);
                } else {
                    // fallback to localStorage
                    const b64 = localStorage.getItem(LS_KEY_BACKUP);
                    if (b64) {
                        try {
                            resolve(base64ToArrayBuffer(b64));
                        } catch (e) {
                            console.warn("Failed to parse key from localStorage backup:", e);
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                }
            };
            req.onerror = () => {
                // fallback to localStorage
                const b64 = localStorage.getItem(LS_KEY_BACKUP);
                if (b64) {
                    try {
                        resolve(base64ToArrayBuffer(b64));
                    } catch (e) {
                        console.warn("Failed to parse key from localStorage backup:", e);
                        resolve(null);
                    }
                } else {
                    resolve(null);
                }
            };
        });
    } catch (err) {
        console.warn("loadExportedKey indexdb open failed, try localStorage:", err);
        const b64 = localStorage.getItem(LS_KEY_BACKUP);
        if (b64) {
            try {
                return base64ToArrayBuffer(b64);
            } catch (e) {
                return null;
            }
        }
        return null;
    }
}

// ===============================
// getCryptoKey (import fallback robust)
async function getCryptoKey() {
    if (cryptoKey) return cryptoKey;

    const exported = await loadExportedKey();

    if (exported) {
        try {
            cryptoKey = await crypto.subtle.importKey(
                "raw",
                exported,
                { name: "AES-GCM" },
                true,
                ["encrypt", "decrypt"]
            );
            return cryptoKey;
        } catch (e) {
            console.warn("importKey failed, will regenerate key:", e);
            // fall through to generate new key
        }
    }

    // create new key and save/back it up
    cryptoKey = await crypto.subtle.generateKey(
        { name: "AES-GCM", length: 256 },
        true,
        ["encrypt", "decrypt"]
    );
    const raw = await crypto.subtle.exportKey("raw", cryptoKey);
    await saveExportedKey(raw);
    return cryptoKey;
}

// ===============================
// encrypt/decrypt (ensure correct types)
async function encrypt(value) {
    const key = await getCryptoKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(String(value));

    const cipherBuffer = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoded);

    return {
        cipher: cipherBuffer,    // ArrayBuffer
        iv: iv.buffer            // ArrayBuffer
    };
}

async function decrypt(cipherBuffer, ivBuffer) {
    const key = await getCryptoKey();

    // Normalize inputs:
    const cipherArr = (cipherBuffer instanceof ArrayBuffer) ? new Uint8Array(cipherBuffer) :
        (cipherBuffer instanceof Uint8Array) ? cipherBuffer :
            (cipherBuffer && cipherBuffer.buffer instanceof ArrayBuffer) ? new Uint8Array(cipherBuffer.buffer) :
                null;

    const ivArr = (ivBuffer instanceof ArrayBuffer) ? new Uint8Array(ivBuffer) :
        (ivBuffer instanceof Uint8Array) ? ivBuffer :
            (ivBuffer && ivBuffer.buffer instanceof ArrayBuffer) ? new Uint8Array(ivBuffer.buffer) :
                null;

    if (!cipherArr || !ivArr) throw new Error("Invalid cipher or iv format");

    const decodedBuffer = await crypto.subtle.decrypt({ name: "AES-GCM", iv: ivArr }, key, cipherArr);
    return new TextDecoder().decode(decodedBuffer);
}

// ===============================
// saveRefreshToken (also backup raw in localStorage as base64)
// ===============================
export async function saveRefreshToken(token) {
    if (!token) return false;
    const encrypted = await encrypt(token);

    try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        store.put({
            cipher: encrypted.cipher,  // ArrayBuffer
            iv: encrypted.iv           // ArrayBuffer
        }, TOKEN_KEY);

        // backup raw token into localStorage as base64 (optional)
        try {
            localStorage.setItem(LS_TOKEN_BACKUP, btoa(String(token)));
        } catch (e) {
            console.warn("Could not backup token to localStorage:", e);
        }

        return await new Promise((resolve, reject) => {
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => reject(tx.error);
        });
    } catch (err) {
        console.warn("saveRefreshToken indexeddb failed, fallback to localStorage", err);
        // fallback store raw token base64 in localStorage
        try {
            localStorage.setItem(LS_TOKEN_BACKUP, btoa(String(token)));
            return true;
        } catch (e) {
            throw e;
        }
    }
}

// ===============================
// getRefreshToken (try IndexedDB -> fallback localStorage)
export async function getRefreshToken() {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).get(TOKEN_KEY);

        return await new Promise((resolve) => {
            req.onsuccess = async () => {
                const result = req.result;
                if (!result) {
                    // fallback to localStorage backup (raw base64)
                    const b64 = localStorage.getItem(LS_TOKEN_BACKUP);
                    if (b64) {
                        try {
                            resolve(atob(b64));
                        } catch (e) {
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                    return;
                }

                // result may contain ArrayBuffer / Uint8Array
                const { cipher, iv } = result;

                try {
                    const token = await decrypt(cipher, iv);
                    resolve(token);
                } catch (err) {
                    console.warn("Token decrypt failed → clearing storage", err);
                    // clear broken stored items and fallback to localStorage raw
                    await clearRefreshToken();
                    const b64 = localStorage.getItem(LS_TOKEN_BACKUP);
                    if (b64) {
                        try {
                            resolve(atob(b64));
                        } catch (e) {
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                }
            };

            req.onerror = () => {
                // fallback to localStorage
                const b64 = localStorage.getItem(LS_TOKEN_BACKUP);
                if (b64) {
                    try { resolve(atob(b64)); } catch (e) { resolve(null); }
                } else {
                    resolve(null);
                }
            };
        });
    } catch (err) {
        console.warn("getRefreshToken fallback localStorage:", err);
        const b64 = localStorage.getItem(LS_TOKEN_BACKUP);
        if (b64) {
            try { return atob(b64); } catch (e) { return null; }
        }
        return null;
    }
}

// ===============================
// clearRefreshToken (also clear localStorage backup)
export async function clearRefreshToken() {
    try {
        const db = await openDB();

        const tx1 = db.transaction(STORE_NAME, "readwrite");
        tx1.objectStore(STORE_NAME).delete(TOKEN_KEY);

        const tx2 = db.transaction(KEY_STORE, "readwrite");
        tx2.objectStore(KEY_STORE).delete(CRYPTO_KEY_ID);

        // clear backups
        localStorage.removeItem(LS_KEY_BACKUP);
        localStorage.removeItem(LS_TOKEN_BACKUP);

        return await Promise.all([
            new Promise(res => { tx1.oncomplete = res; tx1.onerror = res; }),
            new Promise(res => { tx2.oncomplete = res; tx2.onerror = res; })
        ]);
    } catch (err) {
        console.warn("clearRefreshToken fallback:", err);
        try {
            localStorage.removeItem(LS_KEY_BACKUP);
            localStorage.removeItem(LS_TOKEN_BACKUP);
        } catch (e) { }
        return;
    }
}

// optional for testing
export async function nukeDBForTesting() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.deleteDatabase(DB_NAME);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
        req.onblocked = () => alert("Close all tabs using this site then try again");
    });
}
